import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import Button from "@cayuse/shared-react.components.button";
import NumberInput from "@cayuse/shared-react.components.form.inputs.number";
import IconButton from "@cayuse/shared-react.components.icon-button";
import SubHeader from "@cayuse/shared-react.components.sub-header";
import VerticalSpacing from "@cayuse/shared-react.components.vertical-spacing";

import { useTenantFormatter } from "components/BudgetBuilder/shared/TenantFormatter";
import Subsection from "components/Subsection";
import { useDictionary } from "hooks";
import { tenantSelectors } from "store/selectors";
import { updateSubawardCap } from "store/actions/tenant";
import { isPresent, toNumber } from "utils/budgetBuilder";


const SubawardCostCapAmount = () => {
	const { subawardCostCap: instanceCostCap } = useSelector(tenantSelectors.tenant);
	const { currencyFormatter } = useTenantFormatter();

	const dictionary = useDictionary("budgetSettings");
	const [ isLoading, setIsLoading ] = useState(false);
	const [ isEditing, setIsEditing ] = useState(false);
	const [ editedAmount, setEditedAmount ] = useState();

	const isModified = useMemo(
		() => instanceCostCap !== toNumber(editedAmount),
		[ editedAmount, instanceCostCap ],
	);

	useEffect(() => {
		if (isPresent(instanceCostCap)) {
			setEditedAmount(instanceCostCap);
		}
	}, [ instanceCostCap ]);

	const handleSave = () => {
		setIsLoading(true);
		updateSubawardCap(toNumber(editedAmount))
			.finally(() => {
				setIsLoading(false);
				setIsEditing(false);
			});
	};

	const handleReset = () => {
		setEditedAmount(instanceCostCap);
		setIsEditing(false);
	};

	const topButtons = () => {
		const { cancel, save, edit } = dictionary.actions;

		const cancelButton = () => (
			<Button disabled={!isEditing} onClick={handleReset} secondary>
				{cancel || "Cancel"}
			</Button>
		);

		const editSaveButton = () => isEditing ? (
			<IconButton
				key="save"
				// icon="save"
				labelPosition="left"
				disabled={!isModified || !editedAmount}
				busy={isLoading}
				onClick={handleSave}
			>
				{save || "Save"}
			</IconButton>
		) : (
			<IconButton
				key="edit"
				// icon="pencil-alt"
				labelPosition="left"
				onClick={() => setIsEditing(true)}
			>
				{edit || "Edit"}
			</IconButton>
		);

		return ([ cancelButton(), editSaveButton() ]);
	};

	const amountText = useMemo(
		() => currencyFormatter(editedAmount, 0, false),
		[ currencyFormatter, editedAmount ],
	);

	return (
		<Subsection title={dictionary.budgetSettingsTitle || "Budget Settings"} buttons={topButtons()}>
			<SubHeader>{dictionary.budgetSettingsContentHeader || "Subaward F&A Costs Cap Amount"}</SubHeader>
			<p>{dictionary.subawardCostsCapAmount || "Some budgets only allows indirect costs on the first portion of passthrough to Subawardees. Set the cap amount here."}</p>
			{isEditing ? (
				<NumberInput
					value={editedAmount}
					onChange={({ target: { value } }) => setEditedAmount(value)}
				/>
			) : (
				<VerticalSpacing>{amountText}</VerticalSpacing>
			)}
		</Subsection>
	);
};

export default SubawardCostCapAmount;
